/**
 * Ask the studio for a short-lived "save this file" link to a video, audio
 * clip or document that lives in Firebase Storage
 * (netlify/functions/attachmentDownload.mjs has the why).
 *
 * In one line: the page cannot read a Storage file's bytes itself, because
 * the bucket sends no CORS header, so a fetch() of the download URL fails
 * even though a <video> plays it. A signed URL with
 * `Content-Disposition: attachment` lets the browser download it directly,
 * with no CORS and nothing held in the phone's memory.
 *
 * NODE-LOADABLE ON PURPOSE, like lib/saveVideo.ts: every static import names
 * its `.ts` file and none reaches Firebase, so tests run this file itself.
 * The App Check headers are imported lazily, exactly as
 * lib/saveAttachment.ts's photo recovery does.
 */
import { safeHubStorageDownloadUrl } from './hubStorageUrl.ts';
import { parsePreparedDownloadUrl, type PreparedDownloadUrl } from './saveLifecycle.ts';

const ATTACHMENT_DOWNLOAD_ENDPOINT = '/.netlify/functions/attachmentDownload';

/**
 * Mirrors the server's allowlist (cleanAttachmentDownloadName). A name the
 * server would refuse is cleaned here, so a parent's "Recital #2 - Ava's.mov"
 * becomes "Recital 2 - Ava's.mov" instead of a failed save.
 */
const NAME_CHARACTER_OUTSIDE_ALLOWLIST = /[^A-Za-z0-9 ._()'&,+-]+/g;

export function downloadLinkName(fileName: string): string {
  const cleaned = fileName
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(NAME_CHARACTER_OUTSIDE_ALLOWLIST, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  const extension = /\.[a-z0-9]{2,4}$/i.exec(cleaned)?.[0] ?? '';
  const stem = cleaned.slice(0, cleaned.length - extension.length).trim() || 'download';
  return `${stem.slice(0, 120 - extension.length).trim()}${extension}`;
}

export interface AttachmentDownloadLinkDeps {
  fetcher?: typeof fetch;
  headers?: () => Promise<Record<string, string>>;
}

/**
 * Sign a download link for one exact tokenized Storage object. Throws an
 * Error with a sentence a person can act on when the studio cannot sign it.
 */
export async function requestAttachmentDownloadUrl(
  input: { url: string; fileName: string; storagePath?: string },
  deps: AttachmentDownloadLinkDeps = {},
): Promise<PreparedDownloadUrl> {
  const parsed = safeHubStorageDownloadUrl(input.url);
  if (!parsed || (input.storagePath && input.storagePath !== parsed.storagePath)) {
    throw new Error('This file is not available.');
  }
  const fetcher = deps.fetcher ?? fetch;
  const headers = deps.headers
    ? await deps.headers()
    : await import('./functionSecurity').then(({ publicFunctionHeaders }) => publicFunctionHeaders());
  let response: Response;
  try {
    response = await fetcher(ATTACHMENT_DOWNLOAD_ENDPOINT, {
      method: 'POST',
      cache: 'no-store',
      headers,
      body: JSON.stringify({
        url: parsed.url,
        storagePath: parsed.storagePath,
        downloadName: downloadLinkName(input.fileName),
      }),
    });
  } catch {
    throw new Error('Could not reach the studio to save this. Check your connection and try again.');
  }
  const payload = await response.json().catch(() => ({}));
  if (response.status === 429) {
    // Many families on the studio wifi share one address; say so, rather
    // than let the save fall through and blame the connection.
    throw Object.assign(
      new Error('Lots of people are saving right now. Wait a minute, then tap Save again.'),
      { code: 'too_many_requests' },
    );
  }
  const prepared = response.ok ? parsePreparedDownloadUrl(payload) : null;
  if (!prepared) throw new Error('This file could not be saved right now. Try again in a moment.');
  return prepared;
}
